/**
 * Small circular AI-credits gauge for the Velo headers: the ring fills with
 * the share of the period's credits already USED; hovering reveals the exact
 * remaining balance. Always visible once the balance has loaded (unlike the
 * old low-credits-only pill) so users can check their credits at a glance
 * without a taxi-meter number ticking next to the chat.
 *
 * Color escalates with consumption: brand blue → warning at ≥80% used →
 * error when exhausted. Pure presentational component — the host feeds it
 * from useAiCreditsBalance.
 */
import { useTranslations } from "next-intl";
import { HoverInfo } from "@/components/HoverInfo/HoverInfo";
import type { AiCreditsBalance } from "@/hooks/useAiCreditsBalance";

const R = 7;
const CIRCUMFERENCE = 2 * Math.PI * R;

/** Balance, optionally carrying the renewal sentence the hover appends. */
type RingCredits = AiCreditsBalance & { resetLine?: string | null };

export function AiCreditsRing({ credits }: { credits: RingCredits }) {
  const t = useTranslations("aiAssistant");
  if (!credits.loaded) return null;

  // cap <= 0 (a company stamped to a zero quota with no purchased balance)
  // renders as a full exhausted ring — as the header's LEADING icon this
  // must never collapse to nothing.
  const usedShare = credits.cap > 0 ? Math.min(1, credits.used / credits.cap) : 1;
  const colorClass = credits.exhausted
    ? "font-error"
    : credits.low
      ? "font-warning"
      : "font-blue";
  const base = credits.exhausted
    ? t("creditsExhausted")
    : t("creditsRemainingInfo", {
        remaining: credits.remaining,
        cap: credits.cap,
      });
  // When the host knows the renewal story, the hover is where a user checks
  // it — "8 of 50 left" answers a different question than "and then what".
  const label = credits.resetLine ? `${base} · ${credits.resetLine}` : base;

  return (
    // Layout classes live on the HoverInfo wrapper: it renders a plain
    // inline span, and an inline wrapper baseline-aligns the 18px svg —
    // the ring floated visibly above center in the h-12 header rows.
    <HoverInfo className="shrink-0 inline-flex items-center" message={label}>
      <span
        data-testid="ai-credits-ring"
        className={`inline-flex items-center rounded-sm ${colorClass}`}
        role="img"
        aria-label={label}
        // Focusable so the hover card is reachable without a pointer:
        // HoverInfo opens on focus bubbling up from its trigger, and a bare
        // span never receives it. Screen readers already get the full
        // sentence from aria-label; this is for the sighted keyboard user.
        tabIndex={0}
      >
        <svg width={18} height={18} viewBox="0 0 18 18" aria-hidden="true" className="block">
          <circle
            cx={9}
            cy={9}
            r={R}
            fill="none"
            stroke="currentColor"
            strokeOpacity={0.25}
            strokeWidth={2.5}
          />
          {/* Filled arc = share USED; starts at 12 o'clock. A hairline dot is
              kept visible even at 0 used via the round linecap. */}
          <circle
            cx={9}
            cy={9}
            r={R}
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            strokeLinecap="round"
            strokeDasharray={`${CIRCUMFERENCE * usedShare} ${CIRCUMFERENCE}`}
            transform="rotate(-90 9 9)"
          />
        </svg>
      </span>
    </HoverInfo>
  );
}
